import { useEffect, useMemo, useState, useRef } from "react";

export default function useAudioTime(
    audioRef,
    playbackState,
    nowPlaying,
    timeShiftOffsetSec,
    fragProgramDateMs,
    currentTimeRef,
    fragStartSec,
    pausedTimeRef,
) {
    const [audioTimeMs, setAudioTimeMs] = useState(Date.now());
    const lastValidLiveMs = useRef(Date.now());
    const lastValidPlaybackMs = useRef(Date.now());
    const lastUpdateTime = useRef(performance.now());

    const currentLiveMs = useMemo(() => {
        if (!nowPlaying?.currentLiveTime) return lastValidLiveMs.current;
        const ms = nowPlaying?.currentLiveTime ? new Date(nowPlaying.currentLiveTime).getTime() : null;
        if (Number.isFinite(ms)) {
            lastValidLiveMs.current = ms;
            lastUpdateTime.current = performance.now();
            return ms;
        }

        const elapsed = performance.now() - lastUpdateTime.current;
        return lastValidLiveMs.current + elapsed;
    }, [nowPlaying?.currentLiveTime]);

    const bufferStartMs = useMemo(() => {
        if (currentLiveMs == null) return null;
        const maxShift = nowPlaying?.maxTimeShiftSeconds ?? 0;
        return currentLiveMs - maxShift * 1000;
    }, [currentLiveMs, nowPlaying?.maxTimeShiftSeconds]);

    useEffect(() => {
        if (playbackState !== "playing") return;

        let lastUpdate = 0;
        let raf;

        const tick = (now) => {
            if (now - lastUpdate > 250) {
                const base = fragProgramDateMs ?? Date.now();
                const ms = Math.round(base + currentTimeRef.current * 1000 - fragStartSec * 1000);
                setAudioTimeMs(ms);
                lastUpdate = now;
            }
            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playbackState, fragProgramDateMs, fragStartSec]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onTimeUpdate = () => currentTimeRef.current = audio.currentTime || 0;;
        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("seeked", onTimeUpdate);
        audio.addEventListener("loadedmetadata", onTimeUpdate);

        return () => {
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("seeked", onTimeUpdate);
            audio.removeEventListener("loadedmetadata", onTimeUpdate);
        };
    }, []);

    useEffect(() => {
        if (playbackState !== "playing") return;
        const audio = audioRef.current;
        if (!audio) return;

        let interval = setInterval(() => {
            currentTimeRef.current = audio.currentTime || 0;
        }, 1000);

        return () => clearInterval(interval);
    }, [playbackState]);

    const playbackTimeMs = useMemo(() => {
        if (currentLiveMs == null) return lastValidPlaybackMs.current;

        let result;
        if (playbackState === "stopped") result = currentLiveMs;
        else if (playbackState === "paused") result = pausedTimeRef.current ?? currentLiveMs;
        else {
            const baseAudioMs = audioTimeMs == null ? currentLiveMs : audioTimeMs;
            const shiftMs = currentLiveMs - baseAudioMs;
            result = baseAudioMs + shiftMs - timeShiftOffsetSec * 1000;

            if (bufferStartMs != null) {
                result = Math.max(bufferStartMs, Math.min(result, currentLiveMs));
            }
        }

        if (Number.isFinite(result)) {
            lastValidPlaybackMs.current = result;
            return result;
        }

        const elapsed = performance.now() - lastUpdateTime.current;
        return lastValidPlaybackMs.current + elapsed;
    }, [audioTimeMs, playbackState, currentLiveMs, bufferStartMs, timeShiftOffsetSec, pausedTimeRef]);

    return { audioTimeMs, playbackTimeMs, bufferStartMs, currentLiveMs };
}
